import { uuidLocal } from '@/lib/id';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  DialogContent,
  Divider,
  IconButton,
  Radio,
  RadioGroup,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import ImageNotSupportedOutlinedIcon from '@mui/icons-material/ImageNotSupportedOutlined';
import {
  useGetCatalogoQuery,
  useGetLojaAtualQuery,
  type PassoProduto,
  type ProdutoCatalogo,
  type TabelaPrecoCatalogo,
  type TipoCalculoPasso,
} from '../../api';
import { canalDoIdentificador, ehDispositivoFixo, ehVitrine } from '../../lib/canal';
import { passoObrigatorioIndisponivel } from '../../components/catalogo-kiosk/passos';
import { AvisoPassoIndisponivel } from '../../components/aviso-passo-indisponivel';
import { capitalize, formatarBRL } from '../../lib/formato';
import { urlImagemProduto } from '../../lib/imagem';
import { adicionarItem, type ItemCarrinho } from '../../store/carrinho-slice';
import { rastrearAdicionouProduto, rastrearVisualizouProduto } from '../../lib/tracking';
import { EstadoErro, EstadoVazio } from '../../components/estado-mensagem';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import { precoPorQuantidade } from '../../lib/preco-por-quantidade';
import { convitePorQuantidade } from '../../lib/itens-pedido';
import { ConteudoDetalheProduto, DialogDetalhe } from '../../components/detalhe-produto'

interface SelecaoPasso {
  /** mapa idItemTabelaPreco → quantidade selecionada */
  itens: Record<number, number>;
}

export function ProdutoPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const idProduto = Number(id);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { data: loja } = useGetLojaAtualQuery();
  const canal = loja ? canalDoIdentificador(loja.identificador) : undefined;
  const { data, isLoading, error, refetch } = useGetCatalogoQuery(canal!, { skip: !canal });

  const { produto, categoria } = useMemo<{ produto?: ProdutoCatalogo; categoria?: string }>(() => {
    if (!data) return {};
    for (const g of data.grupos) {
      const p = g.produtos.find((x) => x.id === idProduto);
      if (p) return { produto: p, categoria: g.descricao };
    }
    return {};
  }, [data, idProduto]);

  const [quantidadeProduto, setQuantidadeProduto] = useState(1);
  const [observacao, setObservacao] = useState('');
  const [selecoes, setSelecoes] = useState<Record<number, SelecaoPasso>>({});
  const refsPassos = useRef<Record<number, HTMLDivElement | null>>({});

  // Pré-popula itens "adicionar_automaticamente"
  useEffect(() => {
    if (!produto) return;
    const iniciais: Record<number, SelecaoPasso> = {};
    for (const passo of produto.passos) {
      const itensAuto: Record<number, number> = {};
      passo.tabelaPreco?.itens.forEach((it) => {
        if (it.adicionarAutomaticamente) {
          itensAuto[it.id] = it.quantidadeAutomatica ?? 1;
        }
      });
      iniciais[passo.id] = { itens: itensAuto };
    }
    setSelecoes(iniciais);
  }, [produto]);

  // Rastreia "visualizou produto" (ViewContent) quando o produto carrega.
  useEffect(() => {
    if (!produto) return;
    rastrearVisualizouProduto({
      id: produto.id,
      nome: produto.descricao,
      valor: produto.valor,
      categoria,
    });
  }, [produto, categoria]);

  // Fica antes dos early returns para a ordem dos hooks não variar quando o
  // produto ainda não carregou (ex.: acesso direto pela URL). calcularValorPasso
  // é hoisted; selecoes/quantidade entram nas deps.
  const valorTotal = useMemo(() => {
    if (!produto) return 0;
    const passos = [...produto.passos]
      .sort((a, b) => a.ordem - b.ordem)
      .reduce((s, p) => s + calcularValorPasso(p), 0);
    const unitario = precoPorQuantidade(produto.faixasPreco, quantidadeProduto, produto.valor);
    return (unitario + passos) * quantidadeProduto;
  }, [produto, selecoes, quantidadeProduto]);

  const convite = produto
    ? convitePorQuantidade({
        faixasPreco: produto.faixasPreco,
        quantidade: quantidadeProduto,
        precoUnitario: precoPorQuantidade(produto.faixasPreco, quantidadeProduto, produto.valor),
      })
    : null;

  const fechar = (): void => {
    navigate(-1);
  };

  // Envolve o conteúdo no mesmo modal usado no estado carregado, para que
  // loading e "não encontrado" também apareçam como modal (ex.: acesso direto).
  const comoModal = (corpo: JSX.Element): JSX.Element => <DialogDetalhe aoFechar={fechar}>{corpo}</DialogDetalhe>;

  if (isLoading || !loja) {
    return comoModal(
      <DialogContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </DialogContent>,
    );
  }
  // Falha de rede e produto inexistente são coisas diferentes: uma o cliente
  // resolve tentando de novo, a outra não.
  if (error || !data) {
    return comoModal(
      <DialogContent sx={{ pt: 7 }}>
        <EstadoErro aoTentarNovamente={() => void refetch()} />
      </DialogContent>,
    );
  }
  if (!produto) {
    return comoModal(
      <DialogContent sx={{ pt: 7 }}>
        <EstadoVazio
          icone={<SearchOffIcon />}
          titulo="Produto não encontrado"
          descricao="Ele pode ter saído do cardápio. Volte e escolha outro."
        />
      </DialogContent>,
    );
  }

  const passosOrdenados = [...produto.passos].sort((a, b) => a.ordem - b.ordem);
  const passoBloqueado = passoObrigatorioIndisponivel(produto);

  // VITRINE: canal só de exibição — o cliente vê os detalhes do produto, mas não
  // pode adicionar ao carrinho nem editar quantidades.
  const somenteVisualizacao = ehVitrine(loja.identificador);

  function totalSelecionado(passoId: number): number {
    return Object.values(selecoes[passoId]?.itens ?? {}).reduce((s, q) => s + q, 0);
  }

  function passoCompleto(passo: PassoProduto): boolean {
    const total = totalSelecionado(passo.id);
    return total >= passo.quantidadeMinima && total <= passo.quantidadeMaxima;
  }

  function rolarParaProximoPasso(indexAtual: number): void {
    const proximo = passosOrdenados[indexAtual + 1];
    if (!proximo) return;
    const el = refsPassos.current[proximo.id];
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function definirItem(passoIndex: number, passoId: number, itemId: number, novaQtd: number, isRadio: boolean): void {
    setSelecoes((atual) => {
      const sel = { ...(atual[passoId] ?? { itens: {} }) };
      const itens: Record<number, number> = isRadio ? {} : { ...sel.itens };
      if (novaQtd > 0) itens[itemId] = novaQtd;
      else delete itens[itemId];
      sel.itens = itens;
      return { ...atual, [passoId]: sel };
    });
    if (isRadio && novaQtd > 0) rolarParaProximoPasso(passoIndex);
  }

  function calcularValorPasso(passo: PassoProduto): number {
    const sel = selecoes[passo.id]?.itens ?? {};
    const itensSel = (passo.tabelaPreco?.itens ?? []).filter((it) => (sel[it.id] ?? 0) > 0);
    if (itensSel.length === 0) return 0;
    if (passo.tipoCalculo === 'MAIOR_VALOR') {
      return Math.max(...itensSel.map((it) => it.valor));
    }
    if (passo.tipoCalculo === 'FRACAO') {
      const total = itensSel.reduce((s, it) => s + it.valor, 0);
      return total / itensSel.length;
    }
    return itensSel.reduce((s, it) => s + it.valor * (sel[it.id] ?? 0), 0);
  }

  const todosPassosCompletos = passosOrdenados.every(passoCompleto);
  const podeAdicionar = todosPassosCompletos && quantidadeProduto > 0;

  function adicionarAoCarrinho(): void {
    const item: ItemCarrinho = {
      id: uuidLocal(),
      idProduto: produto!.id,
      nome: produto!.descricao,
      quantidade: quantidadeProduto,
      precoUnitario: valorTotal / quantidadeProduto,
      observacao: observacao.trim() || undefined,
      faixasPreco: produto!.faixasPreco,
      passos: passosOrdenados
        .map((passo) => ({
          idProdutoPasso: passo.id,
          descricao: passo.tabelaPreco?.descricao,
          // Contribuição já calculada (respeita FRAÇÃO/MAIOR_VALOR) — a soma dos
          // subitens na integração é reconciliada com este valor.
          valorPasso: calcularValorPasso(passo),
          itens: Object.entries(selecoes[passo.id]?.itens ?? {}).map(([itemId, qtd]) => {
            const it = passo.tabelaPreco?.itens.find((x) => x.id === Number(itemId));
            return {
              idTabelaPrecoItem: Number(itemId),
              idProduto: it?.idProduto ?? 0,
              nome: it?.nomeProduto,
              quantidade: qtd,
              valor: it?.valor ?? 0,
            };
          }),
        }))
        // Passo opcional sem seleção não vai no payload (o backend exige
        // itens.min(1) e não há o que lançar no Sischef).
        .filter((p) => p.itens.length > 0),
    };
    dispatch(adicionarItem(item));
    rastrearAdicionouProduto({
      id: produto!.id,
      nome: produto!.descricao,
      valor: valorTotal / quantidadeProduto,
      quantidade: quantidadeProduto,
      categoria,
    });
    // Volta pro cardápio (não pro carrinho) — fluxo de seguir comprando.
    navigate('/catalogo', { state: { adicionado: true } });
  }

  return (
    <DialogDetalhe aoFechar={fechar}>
      <ConteudoDetalheProduto
        produto={produto}
        observacao={somenteVisualizacao ? undefined : { valor: observacao, aoMudar: setObservacao }}
        quantidade={quantidadeProduto}
        aoMudarQuantidade={setQuantidadeProduto}
        convite={convite}
        acao={
          somenteVisualizacao
            ? undefined
            : { rotulo: `Adicionar · ${formatarBRL(valorTotal)}`, desabilitada: !podeAdicionar, aoAcionar: adicionarAoCarrinho }
        }
      >
        {/* Passo obrigatório sem nenhuma opção (a loja pausou todas): o botão de adicionar já fica
            desabilitado pelo mínimo não atingido — sem este aviso o cliente não descobre por quê.
            Em dispositivo fixo da loja o WhatsApp não ajuda: ali o aviso manda chamar o atendente. */}
        {passoBloqueado && (
          <Box sx={{ mt: 2 }}>
            <AvisoPassoIndisponivel
              nomeProduto={capitalize(produto.descricao)}
              passo={passoBloqueado}
              whatsapp={ehDispositivoFixo(loja.identificador) ? null : loja.configuracao.whatsapp}
            />
          </Box>
        )}

        {passosOrdenados.map((passo, idx) => (
          <Box
            key={passo.id}
            ref={(el) => {
              refsPassos.current[passo.id] = el as HTMLDivElement | null;
            }}
            sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider', scrollMarginTop: 80 }}
          >
            <PassoHeader passo={passo} totalAtual={totalSelecionado(passo.id)} />
            <Divider sx={{ my: 1 }} />
            <PassoItens
              passo={passo}
              selecao={selecoes[passo.id] ?? { itens: {} }}
              somenteLeitura={somenteVisualizacao}
              onChange={(itemId, qtd, isRadio) => definirItem(idx, passo.id, itemId, qtd, isRadio)}
            />
            {passo.tipoCalculo !== 'UNITARIO' && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Cálculo: {passo.tipoCalculo === 'MAIOR_VALOR' ? 'maior valor' : 'média (fracionado)'}
              </Typography>
            )}
          </Box>
        ))}
      </ConteudoDetalheProduto>
    </DialogDetalhe>
  );
}

function PassoHeader({ passo, totalAtual }: { passo: PassoProduto; totalAtual: number }): JSX.Element {
  const obrigatorio = passo.quantidadeMinima > 0;
  const completo = totalAtual >= passo.quantidadeMinima && totalAtual <= passo.quantidadeMaxima;
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Box>
        <Typography variant="subtitle1">{passo.tabelaPreco?.descricao ?? 'Escolha'}</Typography>
        <Typography variant="caption" color="text.secondary">
          {passo.quantidadeMinima === passo.quantidadeMaxima
            ? `Escolha ${passo.quantidadeMinima}`
            : `Escolha de ${passo.quantidadeMinima} a ${passo.quantidadeMaxima}`}
        </Typography>

        {passo.descricaoCardapio && (
          <Typography variant="caption" color="text.secondary" display="block">
            {passo.descricaoCardapio}
          </Typography>
        )}
      </Box>
      <Chip
        size="small"
        label={obrigatorio ? (completo ? 'OK' : 'Obrigatório') : 'Opcional'}
        color={completo ? 'success' : obrigatorio ? 'warning' : 'default'}
      />
    </Stack>
  );
}

export function PassoItens({
  passo,
  selecao,
  somenteLeitura,
  onChange,
}: {
  passo: PassoProduto;
  selecao: SelecaoPasso;
  somenteLeitura?: boolean;
  onChange: (itemId: number, novaQtd: number, isRadio: boolean) => void;
}): JSX.Element {
  const tabela = passo.tabelaPreco;
  if (!tabela) return <Alert severity="warning">Sem itens cadastrados</Alert>;
  // Passo que veio vazio porque a loja pausou todas as opções. O aviso com o contato da loja
  // aparece uma vez, no topo da tela: repetir o botão do WhatsApp em cada passo seria barulho.
  if (passo.indisponivel) {
    return <Alert severity="warning">Nenhuma opção disponível agora.</Alert>;
  }
  // Ordena as opções por `ordem` (nulos por último); a API já ordena, mas mantemos o
  // sort aqui igual aos passos, para não depender da ordem de chegada do backend.
  const itensOrdenados = [...tabela.itens].sort(
    (a, b) => (a.ordem ?? Number.MAX_SAFE_INTEGER) - (b.ordem ?? Number.MAX_SAFE_INTEGER),
  );
  // VITRINE: só lista os complementos disponíveis, sem controles de seleção.
  if (somenteLeitura) {
    return (
      <Stack spacing={1}>
        {itensOrdenados.map((it) => (
          <RotuloItem key={it.id} tabela={tabela} idItem={it.id} tipoCalculo={passo.tipoCalculo} />
        ))}
      </Stack>
    );
  }
  const isRadio = passo.quantidadeMaxima === 1;
  const total = Object.values(selecao.itens).reduce((s, q) => s + q, 0);
  const alinhadoAoTopo = itensOrdenados.some((it) => it.descricao?.trim());
  if (isRadio) {
    const selecionado = Object.keys(selecao.itens)[0];
    return (
      <RadioGroup
        value={selecionado ?? ''}
        onChange={(_e, value) => onChange(Number(value), 1, true)}
      >
        {itensOrdenados.map((it) => (
          <FormControlLabel
            key={it.id}
            value={String(it.id)}
            control={<Radio />}
            label={<RotuloItem tabela={tabela} idItem={it.id} tipoCalculo={passo.tipoCalculo} />}
            sx={alinhadoAoTopo ? { alignItems: 'flex-start', '& .MuiRadio-root': { pt: 0.25 } } : undefined}
          />
        ))}
      </RadioGroup>
    );
  }
  return (
    <Stack spacing={1}>
      {itensOrdenados.map((it) => {
        const qtd = selecao.itens[it.id] ?? 0;
        const podeAumentar = total < passo.quantidadeMaxima;
        return (
          <Stack key={it.id} direction="row" alignItems={alinhadoAoTopo ? 'flex-start' : 'center'} spacing={1}>
            <Box sx={{ flexGrow: 1 }}>
              <RotuloItem tabela={tabela} idItem={it.id} tipoCalculo={passo.tipoCalculo} />
            </Box>
            <IconButton size="small" disabled={qtd === 0} onClick={() => onChange(it.id, qtd - 1, false)}>
              <RemoveIcon fontSize="small" />
            </IconButton>
            <Typography sx={{ minWidth: 20, textAlign: 'center' }}>{qtd}</Typography>
            <IconButton size="small" disabled={!podeAumentar} onClick={() => onChange(it.id, qtd + 1, false)}>
              <AddIcon fontSize="small" />
            </IconButton>
          </Stack>
        );
      })}
    </Stack>
  );
}

/** Lado da miniatura da opção de passo. Ao ImageBoss pedimos o dobro, por causa da tela retina. */
const LADO_MINIATURA_OPCAO = 48;

function RotuloItem({
  tabela,
  idItem,
  tipoCalculo,
}: {
  tabela: TabelaPrecoCatalogo;
  idItem: number;
  tipoCalculo: TipoCalculoPasso;
}): JSX.Element {
  const item = tabela.itens.find((it) => it.id === idItem);
  if (!item) return <></>;
  const descricao = item.descricao?.trim();
  // A miniatura só entra quando alguma opção DO PASSO tem foto: quase nenhuma loja
  // cadastra imagem em adicional, e uma foto solta no meio da lista desalinha o resto.
  const mostrarImagem = tabela.itens.some((it) => !!it.imagem);
  const urlImagem = urlImagemProduto(item.imagem, {
    largura: LADO_MINIATURA_OPCAO * 2,
    altura: LADO_MINIATURA_OPCAO * 2,
    modo: 'cover',
  });

  const rotulo = (
    <Box>
      <Stack direction="row" alignItems="baseline" spacing={1}>
        <Typography variant="body1">{item.nomeProduto}</Typography>
        {tipoCalculo === 'UNITARIO' && item.valor > 0 && (
          <Typography variant="caption" color="text.secondary">
            + {formatarBRL(item.valor)}
          </Typography>
        )}
        {tipoCalculo !== 'UNITARIO' && (
          <Typography variant="caption" color="text.secondary">
            {formatarBRL(item.valor)}
          </Typography>
        )}
      </Stack>
      {descricao && (
        <Typography variant="body2" color="text.secondary">
          {descricao}
        </Typography>
      )}
    </Box>
  );

  if (!mostrarImagem) return rotulo;

  return (
    <Stack direction="row" alignItems="center" spacing={1.5}>
      <Box
        sx={{
          width: LADO_MINIATURA_OPCAO,
          height: LADO_MINIATURA_OPCAO,
          flexShrink: 0,
          borderRadius: 1.5,
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'action.hover',
        }}
      >
        {urlImagem ? (
          <Box
            component="img"
            src={urlImagem}
            alt={item.nomeProduto}
            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <ImageNotSupportedOutlinedIcon sx={{ fontSize: 20, opacity: 0.24 }} />
        )}
      </Box>
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>{rotulo}</Box>
    </Stack>
  );
}
